import clsx from "clsx";
import { formatCurrency } from "@/lib/money";

function formatSigned(amount: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    signDisplay: "exceptZero",
  }).format(amount);
}
import {
  type CashDenomination,
  type CashDenominationCounts,
  denominationKey,
  EUR_COIN_DENOMINATIONS,
  EUR_NOTE_DENOMINATIONS,
} from "@/lib/cashDenominations";

function CounterRow({
  denomination,
  count,
  currency,
  disabled,
  onCountChange,
}: {
  denomination: CashDenomination;
  count: number;
  currency: string;
  disabled?: boolean;
  onCountChange: (key: string, count: number) => void;
}) {
  const key = denominationKey(denomination.value);
  const subtotal = denomination.value * count;

  function setCount(next: number) {
    onCountChange(key, next > 0 ? Math.floor(next) : 0);
  }

  return (
    <div
      className={clsx(
        "flex items-center gap-2 rounded-xl px-2 py-1.5",
        count > 0 ? "bg-accent-soft" : "bg-fill-quaternary",
      )}
    >
      <span className="w-14 shrink-0 text-sm font-semibold tabular-nums text-label">
        {denomination.label}
      </span>
      <div className="flex items-center rounded-lg bg-card shadow-ios ring-1 ring-inset ring-separator">
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center text-lg text-label disabled:opacity-30"
          aria-label={`${denomination.label} verringern`}
          disabled={disabled || count <= 0}
          onClick={() => setCount(count - 1)}
        >
          −
        </button>
        <input
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          className="h-10 w-14 border-0 bg-transparent text-center text-base font-semibold tabular-nums text-label outline-none [appearance:textfield] disabled:opacity-60 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          value={count || ""}
          placeholder="0"
          disabled={disabled}
          aria-label={`Anzahl ${denomination.label}`}
          onWheel={(e) => e.currentTarget.blur()}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            setCount(Number.isFinite(parsed) ? parsed : 0);
          }}
        />
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center text-lg text-label disabled:opacity-30"
          aria-label={`${denomination.label} erhöhen`}
          disabled={disabled}
          onClick={() => setCount(count + 1)}
        >
          +
        </button>
      </div>
      <span className="ml-auto min-w-[4.75rem] text-right text-sm font-medium tabular-nums text-label">
        {count > 0 ? formatCurrency(subtotal, currency) : "—"}
      </span>
    </div>
  );
}

function DenominationSection({
  title,
  denominations,
  counts,
  currency,
  disabled,
  onCountChange,
}: {
  title: string;
  denominations: CashDenomination[];
  counts: CashDenominationCounts;
  currency: string;
  disabled?: boolean;
  onCountChange: (key: string, count: number) => void;
}) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
        {title}
      </h3>
      <div className="space-y-1.5">
        {denominations.map((denomination) => {
          const key = denominationKey(denomination.value);
          return (
            <CounterRow
              key={key}
              denomination={denomination}
              count={counts[key] ?? 0}
              currency={currency}
              disabled={disabled}
              onCountChange={onCountChange}
            />
          );
        })}
      </div>
    </div>
  );
}

export function CashDenominationGrid({
  counts,
  currency,
  total,
  expected,
  disabled,
  onCountChange,
}: {
  counts: CashDenominationCounts;
  currency: string;
  total: number;
  expected?: number | null;
  disabled?: boolean;
  onCountChange: (key: string, count: number) => void;
}) {
  const hasCount = total > 0 || disabled;
  const diff = expected != null && hasCount ? Math.round((total - expected) * 100) / 100 : null;
  const matches = diff != null && Math.abs(diff) <= 0.001;

  return (
    <div className="space-y-4">
      <div className="grid gap-6 md:grid-cols-2">
        <DenominationSection
          title="Scheine"
          denominations={EUR_NOTE_DENOMINATIONS}
          counts={counts}
          currency={currency}
          disabled={disabled}
          onCountChange={onCountChange}
        />
        <DenominationSection
          title="Münzen"
          denominations={EUR_COIN_DENOMINATIONS}
          counts={counts}
          currency={currency}
          disabled={disabled}
          onCountChange={onCountChange}
        />
      </div>
      <div
        className={clsx(
          "flex flex-wrap items-end justify-between gap-3 rounded-xl border px-4 py-3",
          diff == null && "border-separator bg-fill-quaternary",
          matches && "border-status-green/40 bg-status-green/10",
          diff != null && !matches && diff < 0 && "border-status-red/40 bg-status-red/10",
          diff != null && !matches && diff > 0 && "border-status-orange/40 bg-status-orange/10",
        )}
        aria-live="polite"
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">Gezählt</p>
          <p className="text-3xl font-semibold tabular-nums tracking-tight text-label">
            {hasCount ? formatCurrency(total, currency) : "—"}
          </p>
        </div>
        {expected != null ? (
          <div className="text-right">
            <p className="text-xs text-label-secondary">
              Soll {formatCurrency(expected, currency)}
            </p>
            <p
              className={clsx(
                "text-lg font-semibold tabular-nums",
                diff == null && "text-label-secondary",
                matches && "text-status-green",
                diff != null && !matches && diff < 0 && "text-status-red",
                diff != null && !matches && diff > 0 && "text-status-orange",
              )}
            >
              {diff == null ? "noch offen" : matches ? "stimmt" : formatSigned(diff, currency)}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
